import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { Link } from 'react-router-dom';
import Reveal from '@/components/base/Reveal';

const cards = [
  {
    n: '01',
    t: 'Personalised Daily Support',
    d: 'Help with the day\u2019s routines, offered at a pace and in a way that respects how each person likes to live.',
    alt: 'Carer and older woman arranging flowers together at a kitchen table',
    img: 'https://public.readdy.ai/ai/img_res/sandbox/d9f57693db1a3f647bcb80a40a4edde0/5fa7b452-069c-4ae4-8f43-36e8851e8c88_dw-svc-01.webp',
  },
  {
    n: '02',
    t: 'Residential Care',
    d: 'A warm, settled home with considered support around the clock and familiar faces every day.',
    alt: 'Older man reading in a warm, calm residential lounge',
    img: 'https://public.readdy.ai/ai/img_res/sandbox/d9f57693db1a3f647bcb80a40a4edde0/5fa7b452-069c-4ae4-8f43-36e8851e8c88_dw-svc-02.webp',
  },
  {
    n: '03',
    t: 'Memory & Wellbeing',
    d: 'Calm surroundings, gentle routines and meaningful activity for people living with memory changes.',
    alt: 'Older woman and carer looking through a photo album together',
    img: 'https://public.readdy.ai/ai/img_res/sandbox/d9f57693db1a3f647bcb80a40a4edde0/5fa7b452-069c-4ae4-8f43-36e8851e8c88_dw-svc-03.webp',
  },
];

export default function CareAccordion() {
  const [active, setActive] = useState(0);
  const [settled, setSettled] = useState(true);
  // Which card shows its description. Only changes while text is fully hidden,
  // so text never reflows/jumps in view.
  const [shown, setShown] = useState(0);
  const timer = useRef<number | undefined>(undefined);
  const target = useRef(0);
  const wrap = useRef<HTMLDivElement>(null);
  const [expW, setExpW] = useState(0);

  useEffect(() => {
    const el = wrap.current;
    if (!el) return undefined;
    // Expanded card width = 2/4 of (row width - 2 gaps). Images are locked to this
    // width so they never re-crop/zoom while cards resize — cards just reveal more.
    const ro = new ResizeObserver(() => setExpW((el.clientWidth - 40) / 2));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const activate = (i: number) => {
    if (i === target.current) return;
    target.current = i;
    window.clearTimeout(timer.current);
    setSettled(false);
    setActive(i);
    // Wait for the 800ms width transition to fully finish before swapping and showing text.
    timer.current = window.setTimeout(() => {
      setShown(i);
      setSettled(true);
    }, 820);
  };

  useEffect(() => () => window.clearTimeout(timer.current), []);

  return (
    <Reveal className="mt-12">
      <div ref={wrap} className="flex flex-col lg:flex-row gap-5 lg:h-[560px]" onMouseLeave={() => activate(0)}>
        {cards.map((c, i) => {
          const on = active === i;
          return (
            <Link
              key={c.n}
              to="/our-care"
              onMouseEnter={() => activate(i)}
              onFocus={() => activate(i)}
              className={`relative block h-[420px] lg:h-full min-w-0 rounded-2xl overflow-hidden cursor-pointer transition-[flex-grow] duration-[800ms] ease-[cubic-bezier(0.65,0,0.35,1)] will-change-[flex-grow] [contain:paint] ${on ? 'lg:grow-[2]' : 'lg:grow'} lg:basis-0`}
            >
              <img
                loading="lazy"
                decoding="async"
                style={expW ? { '--exp': `${expW}px` } as CSSProperties : undefined}
                className="absolute inset-0 w-full h-full object-cover object-top lg:inset-auto lg:top-0 lg:left-1/2 lg:-translate-x-1/2 lg:w-[var(--exp,100%)] lg:max-w-none"
                alt={c.alt}
                title={`${c.t} at Daywell Living`}
                src={c.img}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/65 via-black/10 to-transparent" />
              <div className={`absolute inset-x-0 bottom-0 p-7 md:p-9 text-background-50 transition-opacity ${settled ? 'opacity-100 duration-500' : 'lg:opacity-0 duration-150'}`}>
                <p className="text-[13px] text-background-50/70">{c.n}</p>
                <h3 className="mt-2 text-[24px] md:text-[28px] font-medium leading-tight">{c.t}</h3>
                <div className={shown === i ? 'block' : 'block lg:hidden'}>
                  <p className="mt-3 text-[15px] leading-relaxed text-background-50/80 max-w-sm">{c.d}</p>
                </div>
              </div>
            </Link>
          );
        })}
      </div>
    </Reveal>
  );
}